import { useEffect } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useRouteError,
} from 'react-router'
import { logger } from '#app/utils/logger.client.ts'
import { CodeSnippet } from './application/code-snippet/code-snippet'
import { Button } from './base/buttons/button'

export function GeneralErrorBoundary() {
	const error = useRouteError()
	const isResponse = isRouteErrorResponse(error)

	useEffect(() => {
		// Don't log HTTP error responses (404, 401, etc.) to Sentry
		if (isResponse) return

		logger.error(
			'Unhandled error in error boundary',
			error instanceof Error ? error : { error },
			{
				component: 'ErrorBoundary',
				tags: { errorType: isResponse ? 'route-error' : 'unexpected-error' },
			},
		)
	}, [error, isResponse])

	switch ((error as ErrorResponse)?.status) {
		case 401:
			return <Unauthorized />
		case 403:
			return <Unauthorized />
		case 404:
			return <NotFound404 />
		default:
			return <ServerError500 />
	}
}

function ErrorMessage() {
	const isDev = process.env.NODE_ENV === 'development'
	const error = useRouteError()

	return (
		isDev && (
			<CodeSnippet
				showLineNumbers={false}
				code={
					error instanceof Error
						? error.message
						: typeof error === 'object'
							? JSON.stringify(error, null, 2)
							: String(error)
				}
				language="plaintext"
			/>
		)
	)
}

function NotFound404() {
	return (
		<section className="bg-primary flex min-h-screen items-start md:items-center">
			<div className="max-w-container mx-auto grow px-4 md:px-8">
				<div className="flex w-full flex-col gap-8 md:gap-12">
					<div className="flex flex-col gap-4 md:gap-6">
						<div className="flex flex-col gap-3">
							<span className="text-md text-brand-secondary font-semibold">
								404 error
							</span>
							<h1 className="text-display-md text-primary md:text-display-lg lg:text-display-xl font-semibold">
								We can't find that page
							</h1>
						</div>
						<p className="text-tertiary text-lg md:text-xl">
							Sorry, the page you are looking for doesn't exist or has been
							moved.
						</p>
						<ErrorMessage />
					</div>

					<Button size="xl" href="/" className="max-w-max" color="secondary">
						Take me home
					</Button>
				</div>
			</div>
		</section>
	)
}

function ServerError500() {
	return (
		<section className="bg-primary flex min-h-screen items-start md:items-center">
			<div className="max-w-container mx-auto grow px-4 md:px-8">
				<div className="flex w-full flex-col gap-8 md:gap-12">
					<div className="flex flex-col gap-4 md:gap-6">
						<div className="flex flex-col gap-3">
							<span className="text-md text-brand-secondary font-semibold">
								500 error
							</span>
							<h1 className="text-display-md text-primary md:text-display-lg lg:text-display-xl font-semibold">
								Something went wrong
							</h1>
						</div>
						<p className="text-tertiary text-lg md:text-xl">
							Sorry, something went wrong. Please try again later.
						</p>
						<ErrorMessage />
					</div>
				</div>
			</div>
		</section>
	)
}

function Unauthorized() {
	return (
		<section className="bg-primary flex min-h-screen items-start md:items-center">
			<div className="max-w-container mx-auto grow px-4 md:px-8">
				<div className="flex w-full flex-col gap-8 md:gap-12">
					<div className="flex flex-col gap-4 md:gap-6">
						<div className="flex flex-col gap-3">
							<span className="text-md text-brand-secondary font-semibold">
								401 error
							</span>
							<h1 className="text-display-md text-primary md:text-display-lg lg:text-display-xl font-semibold">
								Unauthorized
							</h1>
						</div>
						<p className="text-tertiary text-lg md:text-xl">
							Sorry, you are not authorized to access this page.
						</p>
						<ErrorMessage />
					</div>

					<Button size="xl" href="/" className="max-w-max" color="secondary">
						Take me home
					</Button>
				</div>
			</div>
		</section>
	)
}
